import React, { useState, useEffect } from 'react';
import {
  XMarkIcon,
  CreditCardIcon,
  DevicePhoneMobileIcon,
  LockClosedIcon,
  ArrowPathIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon
} from '@heroicons/react/24/outline';
import { api } from '../services/api';
import toast from '../utils/toast';

const PaymentModal = ({ isOpen, onClose, selectedPackage, onPaymentSuccess, isGhana = false }) => {
  const [paymentMethod, setPaymentMethod] = useState('mobile_money'); // Only mobile_money supported for now
  const [processing, setProcessing] = useState(false);
  const [isClosing, setIsClosing] = useState(false);
  const [paymentStatus, setPaymentStatus] = useState('idle'); // idle, processing, pending, completed, failed
  const [paymentIntentId, setPaymentIntentId] = useState(null);
  const [statusMessage, setStatusMessage] = useState('');
  const [billingCycle, setBillingCycle] = useState('monthly');
  const [yearlyDiscountPercentage, setYearlyDiscountPercentage] = useState(20);
  const [paymentData, setPaymentData] = useState({
    phone_number: '',
    network: '', // MTN, Vodafone, AirtelTigo
  });

  // Reset state when modal opens/closes
  useEffect(() => {
    if (isOpen) {
      setPaymentStatus('idle');
      setPaymentIntentId(null);
      setStatusMessage('');
      setPaymentData({ phone_number: '', network: '' });
      setBillingCycle('monthly');
      
      // Fetch system settings including yearly discount percentage
      const fetchSystemSettings = async () => {
        try {
          const response = await api.getSystemSettings();
          if (response.data.success && response.data.settings.yearly_discount_percentage) {
            setYearlyDiscountPercentage(response.data.settings.yearly_discount_percentage);
          }
        } catch (error) {
          console.log('Could not fetch system settings, using default discount:', error);
          // Keep default 20% if fetch fails
        }
      };
      
      fetchSystemSettings();
    }
  }, [isOpen]);

  // Poll payment status when payment is pending
  useEffect(() => {
    if (paymentIntentId && paymentStatus === 'pending') {
      const pollInterval = setInterval(async () => {
        try {
          const response = await api.getPaymentStatus(paymentIntentId);
          const intent = response.data.payment_intent;
          
          if (intent.status === 'completed') {
            setPaymentStatus('completed');
            setStatusMessage('Payment successful! Your subscription has been upgraded.');
            clearInterval(pollInterval);
            setTimeout(() => {
              onPaymentSuccess();
            }, 2000);
          } else if (intent.status === 'failed') {
            setPaymentStatus('failed');
            setStatusMessage(intent.failure_reason || 'Payment failed. Please try again.');
            clearInterval(pollInterval);
          }
        } catch (error) {
          console.error('Error polling payment status:', error);
        }
      }, 3000); // Poll every 3 seconds

      return () => clearInterval(pollInterval);
    }
  }, [paymentIntentId, paymentStatus, onPaymentSuccess]);

  const formatPrice = (price) => {
    if (isGhana) {
      return `₵${Number(price).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
    }
    return `$${Number(price).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  const getPackagePrice = () => {
    if (!selectedPackage) return '';
    
    // For NpontuPay, we always use GHS pricing
    const basePrice = selectedPackage.pricing?.local || selectedPackage.price_ghs || selectedPackage.pricing?.usd || selectedPackage.price_usd;
    
    // Calculate discount multiplier from percentage
    const discountMultiplier = (100 - yearlyDiscountPercentage) / 100;
    const finalPrice = billingCycle === 'yearly' ? basePrice * 12 * discountMultiplier : basePrice;
    
    return `₵${Number(finalPrice).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  const handleClose = () => {
    if (processing) return; // Prevent closing during payment

    setIsClosing(true);
    setTimeout(() => {
      setIsClosing(false);
      onClose();
      resetForm();
    }, 300);
  };

  const resetForm = () => {
    setPaymentData({ phone_number: '', network: '' });
    setPaymentMethod('mobile_money');
    setProcessing(false);
    setPaymentStatus('idle');
    setPaymentIntentId(null);
    setStatusMessage('');
    setBillingCycle('monthly');
  };

  const handleInputChange = (field, value) => {
    setPaymentData(prev => ({ ...prev, [field]: value }));
  };

  const formatCardNumber = (value) => {
    // Remove spaces and non-digits
    const cleaned = value.replace(/\D/g, '');
    // Add spaces every 4 digits
    const formatted = cleaned.replace(/(\d{4})(?=\d)/g, '$1 ');
    return formatted.substring(0, 19); // Max 16 digits + 3 spaces
  };

  const handlePhoneNumberChange = (e) => {
    const value = e.target.value.replace(/\D/g, ''); // Remove non-digits
    handleInputChange('phone_number', value);
  };

  const validatePayment = () => {
    if (!selectedPackage || paymentStatus === 'processing' || paymentStatus === 'pending') return false;
    return paymentData.phone_number && paymentData.network;
  };

  const handlePayment = async () => {
    if (!validatePayment()) {
      toast.error('Please fill in all required fields');
      return;
    }

    setProcessing(true);
    setPaymentStatus('processing');

    try {
      const response = await api.initiateNpontuPayment(
        selectedPackage.id,
        paymentData.phone_number,
        billingCycle
      );

      if (response.data.success) {
        setPaymentIntentId(response.data.payment_intent_id);
        setPaymentStatus('pending');
        setStatusMessage(response.data.message);
        toast.success('Payment request sent! Please check your phone.');
      } else {
        setPaymentStatus('failed');
        setStatusMessage(response.data.error);
        toast.error(response.data.error);
      }

    } catch (error) {
      console.error('Payment error:', error);
      setPaymentStatus('failed');
      const errorMessage = error.response?.data?.error || 'Payment initiation failed. Please try again.';
      setStatusMessage(errorMessage);
      toast.error(errorMessage);
    } finally {
      setProcessing(false);
    }
  };

  // Handle backdrop click
  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) {
      handleClose();
    }
  };

  if (!isOpen && !isClosing) return null;

  return (
    <div
      className={`fixed inset-0 z-50 overflow-y-auto transition-all duration-300 ease-in-out ${
        isOpen && !isClosing ? 'opacity-100' : 'opacity-0'
      }`}
      style={{
        visibility: isOpen || isClosing ? 'visible' : 'hidden'
      }}
    >
      <div
        className="flex items-center justify-center min-h-screen p-4"
        onClick={handleBackdropClick}
      >
        {/* Backdrop */}
        <div
          className={`fixed inset-0 bg-black transition-opacity duration-300 ease-in-out ${
            isOpen && !isClosing ? 'bg-opacity-50' : 'bg-opacity-0'
          }`}
        />

        {/* Modal */}
        <div className={`relative bg-white rounded-2xl shadow-2xl w-full max-w-lg mx-4 transform transition-all duration-300 ease-in-out ${
          isOpen && !isClosing
            ? 'scale-100 translate-y-0 opacity-100'
            : 'scale-95 translate-y-8 opacity-0'
        }`}>
          {/* Header */}
          <div className="flex items-center justify-between p-6 border-b border-gray-200">
            <div className="flex items-center">
              <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
                <CreditCardIcon className="h-6 w-6 text-green-600" />
              </div>
              <div className="ml-3">
                <h3 className="text-lg font-semibold text-gray-900">Upgrade Subscription</h3>
                <p className="text-sm text-gray-500">
                  {selectedPackage?.name} - {getPackagePrice()}
                </p>
              </div>
            </div>
            <button
              onClick={handleClose}
              disabled={processing}
              className="p-2 hover:bg-gray-100 rounded-full transition-colors disabled:opacity-50"
            >
              <XMarkIcon className="h-5 w-5 text-gray-400" />
            </button>
          </div>

          {/* Payment Methods */}
          <div className="p-6">
            {/* Billing Cycle Selection */}
            <div className="mb-6">
              <label className="text-sm font-medium text-gray-700 mb-3 block">
                Billing Cycle
              </label>
              <div className="grid grid-cols-2 gap-3">
                <button
                  onClick={() => setBillingCycle('monthly')}
                  disabled={processing || paymentStatus === 'pending'}
                  className={`flex flex-col items-center justify-center p-4 rounded-lg border-2 transition-all duration-200 ${
                    billingCycle === 'monthly'
                      ? 'border-green-500 bg-green-50 text-green-700 shadow-md'
                      : 'border-gray-200 hover:border-gray-300 text-gray-700 hover:shadow-sm'
                  }`}
                >
                  <span className="font-semibold">Monthly</span>
                  <span className="text-sm text-gray-500">Pay monthly</span>
                </button>
                <button
                  onClick={() => setBillingCycle('yearly')}
                  disabled={processing || paymentStatus === 'pending'}
                  className={`flex flex-col items-center justify-center p-4 rounded-lg border-2 transition-all duration-200 relative ${
                    billingCycle === 'yearly'
                      ? 'border-green-500 bg-green-50 text-green-700 shadow-md'
                      : 'border-gray-200 hover:border-gray-300 text-gray-700 hover:shadow-sm'
                  }`}
                >
                  <div className="absolute -top-2 -right-2 bg-green-500 text-white text-xs px-2 py-1 rounded-full">
                    {yearlyDiscountPercentage}% OFF
                  </div>
                  <span className="font-semibold">Yearly</span>
                  <span className="text-sm text-gray-500">Save {yearlyDiscountPercentage}%</span>
                </button>
              </div>
            </div>

            <div className="mb-6">
              <label className="text-sm font-medium text-gray-700 mb-3 block">
                Payment Method: Mobile Money (Ghana)
              </label>
              <div className="p-4 border-2 border-green-500 bg-green-50 rounded-lg">
                <div className="flex items-center justify-center text-green-700">
                  <DevicePhoneMobileIcon className="h-5 w-5 mr-2" />
                  <span className="font-medium">Mobile Money Payment</span>
                </div>
              </div>
            </div>

            {/* Payment Status Display */}
            {paymentStatus !== 'idle' && (
              <div className={`mb-6 p-4 rounded-lg border-2 transition-all duration-300 ${
                paymentStatus === 'processing' || paymentStatus === 'pending'
                  ? 'border-green-200 bg-green-50'
                  : paymentStatus === 'completed'
                  ? 'border-green-200 bg-green-50'
                  : 'border-red-200 bg-red-50'
              }`}>
                <div className="flex items-center">
                  {(paymentStatus === 'processing' || paymentStatus === 'pending') && (
                    <ArrowPathIcon className="h-5 w-5 text-green-600 animate-spin mr-3" />
                  )}
                  {paymentStatus === 'completed' && (
                    <CheckCircleIcon className="h-5 w-5 text-green-600 mr-3" />
                  )}
                  {paymentStatus === 'failed' && (
                    <ExclamationTriangleIcon className="h-5 w-5 text-red-600 mr-3" />
                  )}
                  <div>
                    <p className={`font-medium ${
                      paymentStatus === 'processing' || paymentStatus === 'pending'
                        ? 'text-green-800'
                        : paymentStatus === 'completed'
                        ? 'text-green-800'
                        : 'text-red-800'
                    }`}>
                      {paymentStatus === 'processing' && 'Processing Payment...'}
                      {paymentStatus === 'pending' && 'Awaiting Payment Confirmation'}
                      {paymentStatus === 'completed' && 'Payment Successful!'}
                      {paymentStatus === 'failed' && 'Payment Failed'}
                    </p>
                    {statusMessage && (
                      <p className={`text-sm mt-1 ${
                        paymentStatus === 'processing' || paymentStatus === 'pending'
                          ? 'text-green-700'
                          : paymentStatus === 'completed'
                          ? 'text-green-700'
                          : 'text-red-700'
                      }`}>
                        {statusMessage}
                      </p>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* Mobile Money Payment Form */}
            {paymentStatus === 'idle' && (
              <div className="space-y-4 transition-all duration-300 ease-in-out opacity-100 translate-x-0">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Mobile Network
                  </label>
                  <select
                    value={paymentData.network}
                    onChange={(e) => handleInputChange('network', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500 transition-all duration-200"
                    required
                    disabled={processing}
                  >
                    <option value="">Select Network</option>
                    <option value="mtn">MTN Mobile Money</option>
                    <option value="vodafone">Vodafone Cash</option>
                    <option value="airteltigo">AirtelTigo Money</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Phone Number
                  </label>
                  <input
                    type="tel"
                    value={paymentData.phone_number}
                    onChange={handlePhoneNumberChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500 transition-all duration-200"
                    placeholder="0241234567 or 233241234567"
                    required
                    disabled={processing}
                  />
                </div>

                <div className="bg-green-50 border border-green-200 rounded-lg p-4 transform transition-all duration-200 hover:bg-green-100">
                  <div className="flex items-start">
                    <DevicePhoneMobileIcon className="h-5 w-5 text-green-600 mt-0.5 mr-2" />
                    <div className="text-sm text-green-800">
                      <p className="font-medium mb-1">Mobile Money Payment Process:</p>
                      <ol className="list-decimal list-inside space-y-1 text-green-700">
                        <li>You'll receive a payment prompt on your phone</li>
                        <li>Enter your mobile money PIN to authorize</li>
                        <li>Your subscription will be activated immediately</li>
                      </ol>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Security Notice */}
            <div className="mt-6 flex items-center justify-center text-sm text-gray-500">
              <LockClosedIcon className="h-4 w-4 mr-2" />
              Your payment information is secure and encrypted
            </div>

            {/* Action Buttons */}
            <div className="mt-6 flex space-x-3">
              <button
                onClick={handleClose}
                disabled={processing || paymentStatus === 'pending'}
                className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-all duration-200 transform hover:scale-105 disabled:hover:scale-100 disabled:opacity-50"
              >
                {paymentStatus === 'completed' ? 'Close' : 'Cancel'}
              </button>
              {paymentStatus === 'idle' && (
                <button
                  onClick={handlePayment}
                  disabled={!validatePayment() || processing}
                  className="flex-1 bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg font-medium transition-all duration-200 transform hover:scale-105 disabled:hover:scale-100 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center shadow-lg hover:shadow-xl"
                >
                  {processing ? (
                    <>
                      <ArrowPathIcon className="h-4 w-4 mr-2 animate-spin" />
                      Processing...
                    </>
                  ) : (
                    <>
                      <CheckCircleIcon className="h-4 w-4 mr-2" />
                      Pay {getPackagePrice()}
                    </>
                  )}
                </button>
              )}
              {paymentStatus === 'failed' && (
                <button
                  onClick={() => {
                    setPaymentStatus('idle');
                    setStatusMessage('');
                    setPaymentIntentId(null);
                  }}
                  className="flex-1 bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg font-medium transition-all duration-200 transform hover:scale-105 flex items-center justify-center shadow-lg hover:shadow-xl"
                >
                  Try Again
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PaymentModal;